import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Seo } from '../components/Seo';
import Logo from '../components/Logo';
import { Loader2, MailCheck, AlertCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Landing page for the "confirm your new email" link.
 * Restrained/utility visual, same family as ResetPassword — this is an
 * account-security moment, not a delight one.
 */
const ChangeEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid confirmation link. Please request a new email change from Settings.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(`${API_URL}/api/auth/confirm-email-change`, { token });
        if (cancelled) return;
        setNewEmail(data?.email || '');
        setSuccess(true);
        toast.success('Email updated.');

        // Refresh AuthContext so header/Settings immediately show the new email
        // instead of the stale previous address (same class of bug as Prompt 16).
        // Guarded because the visitor may not be logged in on this device.
        try {
          await refreshUser();
        } catch (refreshErr) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('refreshUser after confirm-email-change failed:', refreshErr);
          }
        }
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Confirmation failed. The link may have expired.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="change-email-page">
      <Seo noindex />
      <header className="p-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <Logo />
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-0 shadow-none bg-transparent">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-3xl font-heading font-semibold">
              {loading ? 'Confirming…' : success ? 'Email updated' : 'Confirmation failed'}
            </CardTitle>
            <CardDescription>
              {loading
                ? 'Just a moment while we confirm your new email address.'
                : success
                  ? 'Your account email has been changed. A notification has been sent to your previous address.'
                  : error}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-8" data-testid="change-email-loading">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : success ? (
              <div className="text-center py-6" data-testid="change-email-success">
                <div className="mx-auto w-12 h-12 rounded-full border border-foreground/15 flex items-center justify-center mb-4">
                  <MailCheck className="w-6 h-6 text-foreground" />
                </div>
                {newEmail && (
                  <p className="text-sm text-muted-foreground mb-4">
                    All future notifications will go to <strong className="text-foreground">{newEmail}</strong>.
                  </p>
                )}
                <Button className="rounded-full" onClick={() => navigate('/dashboard')} data-testid="change-email-continue-btn">
                  Continue to dashboard
                </Button>
              </div>
            ) : (
              <div className="text-center py-6" data-testid="change-email-error">
                <div className="mx-auto w-12 h-12 rounded-full border border-foreground/15 flex items-center justify-center mb-4">
                  <AlertCircle className="w-6 h-6 text-foreground" />
                </div>
                <Link to="/settings">
                  <Button variant="outline" className="rounded-full" data-testid="change-email-back-to-settings-btn">
                    Back to settings
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-center">
            <Link
              to="/dashboard"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to app
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default ChangeEmail;
